import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * <cart-upsell-list> — sugerencias de 2-3 productos complementarios coherentes con
 * lo que ya hay en el carrito (vestido -> bolso, jersey/punto -> pantalón,
 * chaqueta/abrigo -> bolso, calzado -> bolso, bolso/joya/gafas entre sí), usando
 * únicamente colecciones reales de Yina Studio (sin apps, sin datos inventados).
 * Excluye productos ya presentes en el carrito y variantes no disponibles según
 * Shopify (esa disponibilidad es la que expone la API de Shopify, no una
 * confirmación de stock real del proveedor). No añade nada automáticamente: siempre
 * requiere un clic explícito, y si el producto tiene más de una variante, exige
 * elegirla antes de habilitar "Añadir". No depende en absoluto del precio: la
 * clasificación es solo por categoría de producto, nunca por importe (el mínimo de
 * 60€ es exclusivo del descuento YINA10, no de este componente). Tampoco depende del
 * popup de bienvenida ni de si se aplicó YINA10 — ninguno de los dos aparece en este
 * archivo en absoluto.
 *
 * Este elemento renderiza TODO su contenido (título incluido) por JS. Si no hay
 * sugerencias válidas, se vacía y se oculta por completo (título incluido, sin dejar
 * hueco) — ver _clear().
 *
 * CORREGIDO 30 sep (bug de precio): /collections/<handle>/products.json devuelve
 * variant.price como cadena decimal en la moneda de la tienda, NO en céntimos.
 *
 * CORREGIDO 30 sep (segunda vez): data-skip-node-update/data-skip-subtree-update
 * (ver assets/morph.js) para que el motor de morphing del tema nunca borre las
 * tarjetas ya pintadas por este script en una actualización de carrito.
 *
 * CORREGIDO 1 oct: `refresh()` ya no cachea un fallo de la petición del mapa como si
 * hubiera tenido éxito (antes dejaba las sugerencias ocultas para siempre en esa
 * página tras un solo fallo transitorio). Añadidos reintentos limitados en todas las
 * peticiones y una segunda comprobación automática (_scheduleRefresh) poco después de
 * la primera, para autocorregir cualquier estado incompleto sin acción del cliente.
 *
 * CORREGIDO 1 oct (causa real encontrada — ver snippets/header-actions.liquid): la
 * causa real del fallo al añadir el primer artículo a un carrito vacío sin recargar
 * la página era que la etiqueta <script> que carga ESTE archivo solo existía en el
 * HTML de la plantilla de "carrito con artículos" — mientras el carrito estaba vacío,
 * esa etiqueta no existía, y un <script> insertado después mediante el motor de
 * morphing del tema (en vez de analizado por el navegador al cargar la página) NUNCA
 * se ejecuta — es un comportamiento estándar de cualquier navegador. Solución real:
 * snippets/header-actions.liquid ahora carga este módulo de forma incondicional,
 * independientemente de si el carrito está vacío al cargar la página.
 *
 * CONFIRMADO 1 oct (validación real en ordenador y móvil): con la carga incondicional
 * de arriba, las sugerencias aparecen correctamente al añadir el primer artículo a un
 * carrito vacío, sin recargar la página y sin depender del rasca ni de YINA10, en
 * ambas plataformas.
 *
 * CORREGIDO 28 oct (i18n): el fetch de /collections/<handle>/products.json no llevaba
 * ningún prefijo de idioma, así que SIEMPRE devolvía el título de producto en español
 * (el idioma por defecto de la tienda), incluso navegando en /fr. Ahora se antepone
 * data-root-url (= {{ routes.root_url }}, ya consciente del idioma/mercado, pasado
 * desde snippets/cart-upsell.liquid) a esa URL. Los textos "Elige opción", "Añadir",
 * "Añadiendo…", "No disponible" y "Añadido ✓" (antes fijos en español en este archivo)
 * ahora se leen de data-choose-option/data-add/data-adding/data-unavailable/data-added/
 * data-choose-aria-prefix, traducidos desde locales/es.json y locales/fr.json — con el
 * texto en español de siempre como valor de reserva si faltara el atributo. El formato
 * de precio usaba 'es-ES' fijo; ahora usa el idioma real de la página.
 *
 * CORREGIDO 28 oct (color swatches): variant.title (usado en las <option> del selector
 * de variante de cada tarjeta) viene de products.json sin traducir para los colores con
 * swatch asignado — mismo límite de plataforma que afecta al selector de la ficha de
 * producto y a la línea del carrito (ver snippets/color-name-display.liquid). Se lee
 * data-color-names (JSON con el diccionario content.color_names del idioma actual,
 * pasado desde snippets/cart-upsell.liquid) y _translateVariantTitle() sustituye solo
 * los tokens de color conocidos dentro del título de la variante (separados por " / "),
 * dejando intactos los tokens no reconocidos (tallas) y el id real de la variante usado
 * para añadir al carrito — nunca se toca ese id, solo el texto mostrado.
 */

const MAX_ITEMS = 3;
const MAX_PER_COLLECTION = 10;
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 400;
const TRAILING_REFRESH_DELAY_MS = 700;

class CartUpsellList extends HTMLElement {
  connectedCallback() {
    this._onCartUpdate = this._onCartUpdate.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this._onCartUpdate);
    this._colorNames = this._parseColorNames();
    this._scheduleRefresh();
  }

  _parseColorNames() {
    try {
      return JSON.parse(this.dataset.colorNames || '{}');
    } catch (error) {
      return {};
    }
  }

  _normalizeColorKey(token) {
    return token
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/á/g, 'a')
      .replace(/é/g, 'e')
      .replace(/í/g, 'i')
      .replace(/ó/g, 'o')
      .replace(/ú/g, 'u');
  }

  _translateVariantTitle(title) {
    if (!title || !this._colorNames) return title;
    return title
      .split(' / ')
      .map((token) => this._colorNames[this._normalizeColorKey(token)] || token)
      .join(' / ');
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this._onCartUpdate);
    clearTimeout(this._trailingRefreshTimer);
  }

  _onCartUpdate(event) {
    if (event.target === this) return;
    this._scheduleRefresh();
  }

  _scheduleRefresh() {
    this.refresh();
    clearTimeout(this._trailingRefreshTimer);
    this._trailingRefreshTimer = setTimeout(() => this.refresh(), TRAILING_REFRESH_DELAY_MS);
  }

  _collectionsUrl(handle) {
    const root = (this.dataset.rootUrl || '/').replace(/\/$/, '');
    return `${root}/collections/${handle}/products.json?limit=${MAX_PER_COLLECTION}`;
  }

  async _fetchWithRetry(url) {
    let lastError;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const response = await fetch(url);
        if (response.ok || attempt === MAX_RETRIES) return response;
      } catch (error) {
        lastError = error;
        if (attempt === MAX_RETRIES) throw lastError;
      }
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
    }
    throw lastError || new Error('fetch failed');
  }

  async refresh() {
    const requestId = (this._requestId = (this._requestId || 0) + 1);

    try {
      if (!this._map) {
        const mapRes = await this._fetchWithRetry(this.dataset.mapUrl);
        if (!mapRes.ok) {
          this._clear();
          return;
        }
        this._map = await mapRes.json();
      }

      const cartRes = await this._fetchWithRetry('/cart.js');
      if (!cartRes.ok) {
        this._clear();
        return;
      }
      const cart = await cartRes.json();

      if (requestId !== this._requestId) return;

      if (!cart.items || cart.items.length === 0) {
        this._clear();
        return;
      }

      const cartHandles = new Set(cart.items.map((item) => item.handle));
      const categories = new Set();
      cart.items.forEach((item) => {
        const category = this._map.product_category[item.handle];
        if (category) categories.add(category);
      });

      const targetCollections = new Set();
      categories.forEach((category) => {
        (this._map.category_targets[category] || []).forEach((handle) => targetCollections.add(handle));
      });

      if (targetCollections.size === 0) {
        this._clear();
        return;
      }

      const productLists = await Promise.all(
        Array.from(targetCollections).map((handle) =>
          this._fetchWithRetry(this._collectionsUrl(handle))
            .then((response) => (response.ok ? response.json() : { products: [] }))
            .catch(() => ({ products: [] }))
        )
      );

      if (requestId !== this._requestId) return;

      const seen = new Set();
      const candidates = [];
      productLists.forEach((list) => {
        (list.products || []).forEach((product) => {
          if (cartHandles.has(product.handle) || seen.has(product.handle)) return;
          const availableVariants = (product.variants || []).filter((variant) => variant.available);
          if (availableVariants.length === 0) return;
          seen.add(product.handle);
          candidates.push({ product, availableVariants });
        });
      });

      const picks = candidates.slice(0, MAX_ITEMS);

      if (picks.length === 0) {
        this._clear();
        return;
      }

      this._render(picks);
    } catch (error) {
      console.error('cart-upsell-list', error);
      if (requestId === this._requestId) this._clear();
    }
  }

  _render(picks) {
    const heading = this.dataset.heading || 'También te puede interesar';
    this.innerHTML = `
      <h3 class="cart-upsell__heading h6">${this._escape(heading)}</h3>
      <ul class="cart-upsell__list">${picks.map((pick) => this._renderCard(pick)).join('')}</ul>
    `;
    this.hidden = false;
    this._bindEvents();
  }

  _clear() {
    this.hidden = true;
    this.innerHTML = '';
  }

  _renderCard({ product, availableVariants }) {
    const image = product.images && product.images[0];
    const needsPicker = availableVariants.length > 1;
    const firstVariant = availableVariants[0];
    const chooseOptionText = this.dataset.chooseOption || 'Elige opción';
    const addText = this.dataset.add || 'Añadir';
    const chooseAriaPrefix = this.dataset.chooseAriaPrefix || 'Elegir opción para';

    const options = needsPicker
      ? `<option value="" disabled selected>${this._escape(chooseOptionText)}</option>` +
        availableVariants
          .map(
            (variant) =>
              `<option value="${variant.id}" data-price="${variant.price}">${this._escape(this._translateVariantTitle(variant.title))}</option>`
          )
          .join('')
      : '';

    return `
      <li class="cart-upsell__item" data-product-handle="${product.handle}">
        <a href="/products/${product.handle}" class="cart-upsell__image-link" tabindex="-1">
          ${image ? `<img src="${this._imageUrl(image.src)}" alt="" width="56" height="56" loading="lazy">` : ''}
        </a>
        <div class="cart-upsell__info">
          <a href="/products/${product.handle}" class="cart-upsell__title">${this._escape(product.title)}</a>
          <span class="cart-upsell__price">${this._formatMoney(firstVariant.price)}</span>
          ${needsPicker ? `<select class="cart-upsell__variant-select" aria-label="${this._escape(chooseAriaPrefix)} ${this._escape(product.title)}">${options}</select>` : ''}
          <button
            type="button"
            class="cart-upsell__add button"
            data-variant-id="${needsPicker ? '' : firstVariant.id}"
            ${needsPicker ? 'disabled' : ''}
          >${this._escape(addText)}</button>
        </div>
      </li>
    `;
  }

  _bindEvents() {
    this.querySelectorAll('.cart-upsell__item').forEach((item) => {
      const select = item.querySelector('.cart-upsell__variant-select');
      const button = item.querySelector('.cart-upsell__add');
      const priceEl = item.querySelector('.cart-upsell__price');
      if (!button) return;

      if (select) {
        select.addEventListener('change', () => {
          button.dataset.variantId = select.value;
          button.disabled = select.value === '';

          const selectedOption = select.selectedOptions && select.selectedOptions[0];
          if (priceEl && selectedOption && selectedOption.dataset.price) {
            priceEl.textContent = this._formatMoney(selectedOption.dataset.price);
          }
        });
      }

      button.addEventListener('click', () => this._addToCart(button));
    });
  }

  async _addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    const addText = this.dataset.add || 'Añadir';
    const addingText = this.dataset.adding || 'Añadiendo…';
    const unavailableText = this.dataset.unavailable || 'No disponible';
    const addedText = this.dataset.added || 'Añadido ✓';

    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = addingText;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      const data = await response.json();

      if (!response.ok) {
        button.textContent = unavailableText;
        setTimeout(() => {
          button.textContent = originalText || addText;
          button.disabled = false;
        }, 2000);
        return;
      }

      button.textContent = addedText;
      document.dispatchEvent(new CartAddEvent(data, 'cart-upsell-list', { source: 'cart-upsell' }));
      setTimeout(() => this._scheduleRefresh(), 500);
    } catch (error) {
      console.error('cart-upsell-list add', error);
      button.textContent = originalText || addText;
      button.disabled = false;
    }
  }

  _formatMoney(amount) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    const value = typeof amount === 'string' ? parseFloat(amount) : amount;
    const locale = document.documentElement.lang || 'es-ES';
    return value.toLocaleString(locale, { style: 'currency', currency });
  }

  _imageUrl(src) {
    if (!src) return '';
    return src + (src.includes('?') ? '&' : '?') + 'width=160';
  }

  _escape(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }
}

if (!customElements.get('cart-upsell-list')) {
  customElements.define('cart-upsell-list', CartUpsellList);
}
